'use client';

import { useEffect, useRef, type RefObject } from 'react';

const ACTIVATABLE_SELECTOR = 'a[href], button, [role="button"]';

export interface LastActivation {
  /** Last button or link activated outside any modal and outside `excludeRef`. */
  element: HTMLElement | null;
  /**
   * Whether the most recent activation came from the keyboard (Enter / Space
   * dispatch a `click` with `detail === 0`; a pointer click has `detail >= 1`).
   */
  keyboard: boolean;
}

/**
 * Remembers how and from where an overlay was opened. Pair with
 * `useFocusTrap`'s `restoreFallback` so an overlay can hand focus back to the
 * control that opened it even when that control was disabled while the
 * overlay loaded. Inputs are never recorded, so closing never drops a caret
 * into a field. Clicks inside another modal (e.g. "Continue to cart" in the
 * upsell, which unmounts as the cart opens) update `keyboard` but keep the
 * previous `element`, which is still on the page.
 */
export function useLastActivatedElement(
  excludeRef: RefObject<HTMLElement | null>,
): RefObject<LastActivation> {
  const lastRef = useRef<LastActivation>({ element: null, keyboard: false });

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;
      if (excludeRef.current?.contains(event.target)) return;
      const el = event.target.closest<HTMLElement>(ACTIVATABLE_SELECTOR);
      if (!el) return;
      lastRef.current.keyboard = event.detail === 0;
      if (!el.closest('[aria-modal="true"]')) lastRef.current.element = el;
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [excludeRef]);

  return lastRef;
}
